import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button, Stack, TextField, Typography } from '@mui/material';
import { client } from '../../api/graphql';
import { SET_GUARDIAN_DISPLAY_NAME } from '../../api/graphql_guardian_name';

/**
 * Mandatory display-name entry (route: /#name?next=…).
 *
 * Phone-auth accounts have no name; `decidePostAuthRoute` sends fresh
 * parents here before letting them into the app. On submit we persist the
 * name and continue to the `next` destination the router would otherwise
 * have chosen (default /profile).
 *
 * NOTE: this app has no global <ApolloProvider> — the shared Apollo
 * `client` is imported and called directly (client.mutate), the same
 * pattern the react-query api hooks use. Using Apollo's useMutation here
 * throws "no client in context".
 */
export default function NamePrompt(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();

  const next =
    new URLSearchParams(location.search).get('next') || '/profile';

  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = name.trim();
  const canSubmit = trimmed.length > 0 && !saving;

  const handleContinue = async () => {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      await client.mutate({
        mutation: SET_GUARDIAN_DISPLAY_NAME,
        variables: { name: trimmed },
      });
      navigate(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save your name.');
      setSaving(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#F8FAFC',
        px: 3,
      }}
    >
      <Box
        sx={{
          width: '100%',
          maxWidth: 420,
          bgcolor: '#fff',
          borderRadius: 4,
          p: 4,
          boxShadow: '0 10px 40px rgba(15,23,42,0.08)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography variant="h5" fontWeight={800}>
              What's your name?
            </Typography>
            <Typography color="text.secondary">
              We'll show this instead of your phone number.
            </Typography>
          </Stack>

          <TextField
            autoFocus
            fullWidth
            label="Your name"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') void handleContinue();
            }}
            disabled={saving}
          />

          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}

          <Button
            fullWidth
            variant="contained"
            size="large"
            disabled={!canSubmit}
            onClick={() => void handleContinue()}
            sx={{ borderRadius: 999, py: 1.4, textTransform: 'none', fontWeight: 700 }}
          >
            {saving ? 'Saving…' : 'Continue'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
